import type { CreateLoopInput, LoopCadence, LoopState, LoopView } from '../../../apps/apis/commandCenter/types';
import type { AgentCard, AgentGroup } from '../types';

/**
 * Every state a loop may move to from here. The server accepts any move but the one it is already in,
 * so the board offers the same — a ladder shown as a gate would read as a downgrade from Linear, where
 * any status could be set from any other. LOOP_STATE_ORDER still orders the list so the usual path
 * reads first.
 */
export const nextStatesFrom = (state: LoopState): LoopState[] =>
  LOOP_STATE_ORDER.filter((candidate) => candidate !== state);

/** Ladder order for the board, so states read left to right the way work actually moves. */
export const LOOP_STATE_ORDER: readonly LoopState[] = [
  'idea',
  'approved',
  'building',
  'verifying',
  'running',
  'paused',
  'retired',
];

export const LOOP_STATE_LABELS: Readonly<Record<LoopState, string>> = {
  idea: 'Idea',
  approved: 'Approved',
  building: 'Building',
  verifying: 'Verifying',
  running: 'Running',
  paused: 'Paused',
  retired: 'Retired',
};

export const LOOP_CADENCES: readonly LoopCadence[] = ['hourly', 'daily', 'weekly', 'monthly', 'quarterly', 'ad_hoc'];

/** Schema words the team does not speak; the form shows these instead. */
export const LOOP_CADENCE_LABELS: Readonly<Record<LoopCadence, string>> = {
  hourly: 'Every hour',
  daily: 'Every day',
  weekly: 'Every week',
  monthly: 'Every month',
  quarterly: 'Every quarter',
  ad_hoc: 'When needed',
};

/** What the planning form collects. Section comes from the "+" that opened it, not from the person. */
export interface LoopDraft {
  name: string;
  objective: string;
  acceptance: string;
  owner: string;
  cadence: string;
  /** 'YYYY-MM-DD' or '' — committing to a day is optional, so blank stays blank. */
  targetDate: string;
  section: string;
}

export interface LoopSectionGroup {
  section: string;
  loops: LoopView[];
}

/** Blank optional fields are omitted rather than sent as '' — an empty string would read as answered. */
export const loopDraftToInput = (draft: LoopDraft): CreateLoopInput => {
  const input: CreateLoopInput = { name: draft.name.trim(), section: draft.section };
  const objective = draft.objective.trim();
  const acceptance = draft.acceptance.trim();
  const owner = draft.owner.trim();
  if (objective) {
    input.objective = objective;
  }
  if (acceptance) {
    input.acceptance = acceptance;
  }
  if (owner) {
    input.owner = owner;
  }
  if (draft.cadence) {
    input.cadence = draft.cadence as LoopCadence;
  }
  if (draft.targetDate) {
    input.targetDate = draft.targetDate;
  }
  return input;
};

/**
 * Groups loops under their category, because that is how the board is read — a loop belongs with the
 * rest of its domain rather than gathered with every other idea in the company. Inside a category they
 * run along the ladder so the furthest along sits last, and categories are ordered by name so the
 * panel does not reshuffle between reads.
 */
export const loopsBySection = (loops: readonly LoopView[]): LoopSectionGroup[] => {
  const bySection = new Map<string, LoopView[]>();
  for (const loop of loops) {
    const bucket = bySection.get(loop.section);
    if (bucket) {
      bucket.push(loop);
    } else {
      bySection.set(loop.section, [loop]);
    }
  }
  return [...bySection.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([section, group]) => ({
      section,
      loops: [...group].sort((a, b) => LOOP_STATE_ORDER.indexOf(a.state) - LOOP_STATE_ORDER.indexOf(b.state)),
    }));
};

/** The server derives this on every read; recomputing it here would let the two drift apart. */
export const needsDetailOf = (loop: LoopView): boolean => loop.needsDetail;

/** Prefix so a loop can never collide with an agent id in the same board namespace. */
const LOOP_CARD_PREFIX = 'loop:';

/**
 * A loop rendered as a board card, so it sorts, folds and reads like everything else in its section.
 * It reuses the planned-card shape the board already draws dashed, and hands the target date to the
 * launch marker that shape already knows how to render.
 */
export const loopToCard = (loop: LoopView): AgentCard => ({
  id: `${LOOP_CARD_PREFIX}${loop.loopId}`,
  section: loop.section,
  name: loop.name,
  goal: loop.objective ?? '',
  goalParts: loop.objective ? [{ text: loop.objective, bold: false }] : [],
  sub: [loop.overdue ? 'late' : '', loop.needsDetail ? 'needs detail' : '', loop.linearIssueId ?? '']
    .filter(Boolean)
    .join(' · '),
  dot: '',
  badge: { text: loop.state, className: loop.overdue ? 'b-crit' : 'b-idle' },
  sched: loop.cadence ? `runs ${loop.cadence}` : 'not scheduled',
  tags: [],
  owner: loop.owner ?? 'unowned',
  runs: 0,
  // Nothing here may restate the state — the badge already says that, and a stats row that reads
  // "planned" on a building loop is the tile contradicting itself.
  runsText: '–',
  runsLabel: 'no runs yet',
  succText: loop.cadence ? `runs ${loop.cadence}` : 'cadence unset',
  succTone: loop.cadence ? '' : 'new',
  planned: true,
  // Only targetDate and progress are read off this; the rest satisfies the shape the card draws.
  plan: {
    id: loop.loopId,
    section: loop.section,
    name: loop.name,
    objective: loop.objective ?? '',
    owner: loop.owner ?? '',
    note: '',
    targetDate: loop.targetDate ?? '',
    progress: 0,
    createdAt: 0,
  },
  loop: loopActionsOf(loop),
});

/** The loop controls a tile carries once it is the same work as a loop. */
const loopActionsOf = (loop: LoopView) => ({
  loopId: loop.loopId,
  state: loop.state,
  nextStates: nextStatesFrom(loop.state),
  canRemove: loop.state === 'idea',
});

/**
 * The board has one kind of card: a loop. When a loop names an agent the tile is still the loop's —
 * its name, its state, its owner, its cadence — and what it takes from the agent is the half only the
 * agent can report: the runs. It keeps the agent's id so run history and the skill file still resolve,
 * and it stops being drawn as a plan, because something is actually running.
 */
const withRuntime = (loop: LoopView, card: AgentCard): AgentCard => ({
  ...loopToCard(loop),
  id: card.id,
  dot: card.dot,
  tags: card.tags,
  runs: card.runs,
  runsText: card.runsText,
  runsLabel: card.runsLabel,
  succText: card.succText,
  succTone: card.succTone,
  planned: false,
});

/**
 * Drops each loop into the section it belongs to. A loop that names an agent is the same work as that
 * agent, so it lands ON that tile rather than beside it — the plan supplies intent, the agent supplies
 * reality, and two tiles for one job would just be the board disagreeing with itself. A loop naming a
 * section the board does not have is left out rather than conjuring a section nobody defined.
 */
export const mergeLoopCards = (groups: readonly AgentGroup[], loops: readonly LoopView[]): AgentGroup[] =>
  groups.map((group) => {
    const mine = loops.filter((loop) => loop.section === group.slug);
    const wiredByAgent = new Map(
      mine.filter((loop) => loop.agentId !== null).map((loop) => [loop.agentId as string, loop]),
    );
    const cards = group.cards.map((card) => {
      const wired = wiredByAgent.get(card.id);
      return wired ? withRuntime(wired, card) : card;
    });
    return { ...group, cards: [...cards, ...mine.filter((loop) => loop.agentId === null).map(loopToCard)] };
  });
